/**
 * Typed API client: the one place the SPA talks to the `/api` worker. Requests
 * are same-origin so the httpOnly session cookie rides along automatically; the
 * server error envelope (`{ error }`) is surfaced as a thrown `ApiError`.
 */
import { forgetKept } from "./kept";
import { WORKSPACE_HEADER } from "../../shared/workspace-path";
import { APP_ORIGIN, isTenantHost } from "./tenant";
import { currentWorkspaceSlug } from "./workspace";
import type {
  AnnounceRequest,
  CircleCreateRequest,
  CircleCreateResponse,
  DirectSendRequest,
  GuestAnnouncementsResponse,
  GuestCircleResponse,
  GuestEveryoneResponse,
  GuestPlace,
  GuestPrefs,
  GuestTalkResponse,
  HostAnnouncementsResponse,
  HostCircleResponse,
  HostDirectResponse,
  HostEveryoneResponse,
  HostFilesResponse,
  HostRoomRow,
  HostTalkResponse,
  PrivateSendRequest,
  PrivateSendResponse,
  QuestionAnswerRequest,
  QuestionDraftRequest,
  QuestionDraftResponse,
  QuestionAnswerResponse,
  SeenRequest,
  TalkAnnouncement,
  TalkAnswerGroup,
  TalkAnswersResponse,
  TalkFile,
  TalkMessage,
  TalkQuestionsResponse,
  TalkSearchResponse,
  TalkSendRequest,
} from "../../shared/talk";
import type {
  RoomLanguageRequest,
  RoomLanguageResponse,
  EditMessageRequest,
  NoteUploadAbortRequest,
  PinRequest,
  SignupAnswerRequest,
  WatchPin,
  ApiKeyInfo,
  AutomationDetail,
  AutomationListResponse,
  AutomationPeople,
  AutomationsSettingsRequest,
  BillingAlertResponse,
  BillingLiveNoticeResponse,
  BoardCardRequest,
  BoardCardResponse,
  BoardChaptersRequest,
  BoardLinesResponse,
  BoardLive,
  BoardLiveTimeRequest,
  BoardNamesFixRequest,
  BoardNamesResponse,
  BoardPublishResponse,
  BoardReadRequest,
  BoardSaveRequest,
  BoardSaveResponse,
  BoardStartLiveRequest,
  BoardStartRequest,
  BoardStartResponse,
  BoardStateResponse,
  BoardTranscribeRequest,
  BoardTranscribeResponse,
  BoardUnpublishResponse,
  ConfirmationToggleRequest,
  ConfirmationToggleResponse,
  CreateAutomationRequest,
  CustomDomainRequest,
  DraftReplyResponse,
  EnrollManyRequest,
  EnrollManyResponse,
  FactCatalog,
  HomeDoneRequest,
  HomeRange,
  HomeResponse,
  InviteAudience,
  InviteDraft,
  InviteOptions,
  InvitePreview,
  InviteSendRequest,
  InviteSendResponse,
  InviteTestRequest,
  InvitesResponse,
  IssuedKeyResponse,
  LeaveSeriesRequest,
  LibraryAsset,
  LibraryAssetPatch,
  LibraryFolder,
  LibraryFolderPatch,
  LibraryFolderRequest,
  LibraryResponse,
  LibraryUploadCompleteRequest,
  LibraryUploadStartRequest,
  LibraryUploadStartResponse,
  LibraryUseResponse,
  LoveResponse,
  LiveChangeResponse,
  LiveDoorResponse,
  LiveEndResponse,
  LiveJoinResponse,
  LiveMomentRequest,
  LiveMomentResponse,
  LivePeopleResponse,
  LiveSeriesRequest,
  LiveSessionPatch,
  LiveStudio,
  LiveTicketRequest,
  LiveTicketResponse,
  LogoutResponse,
  MeResponse,
  NoteStatusResponse,
  NoteUploadCompleteRequest,
  NoteUploadCompleteResponse,
  NoteUploadSignRequest,
  NoteUploadSignResponse,
  NoteUploadStartRequest,
  NoteUploadStartResponse,
  OfferJson,
  OfferRequest,
  OfferResponse,
  OfferVideoRequest,
  OkResponse,
  PageArrangeResponse,
  PageAskRequest,
  PageAskResponse,
  PageBackRequest,
  PageBackResponse,
  PageChooseRequest,
  PageInspirationsRequest,
  PageInspirationsResponse,
  PageMakeRequest,
  PageSlot,
  PageMakeResponse,
  PagePairsResponse,
  PagePhoneRequest,
  PagePhoneResponse,
  PageStudioResponse,
  PageTextRequest,
  NoteRequest,
  PageVideosResponse,
  PeopleEmailDraft,
  PeopleEmailDraftRequest,
  PeopleEmailRequest,
  PeopleEmailResponse,
  PeopleFilters,
  PersonNote,
  PeopleListResponse,
  PersonDetailResponse,
  ProgressRequest,
  PublicCommentRequest,
  PublicLookResponse,
  PublicMessageRequest,
  PublicRecordsResponse,
  PublicRegisterRequest,
  PublicRegisterResponse,
  PutAutomationWebinarsRequest,
  QuickLiveRequest,
  QuickLiveResponse,
  ReactResponse,
  RespondRequest,
  SaveGraphRequest,
  ScriptRequest,
  SendingDomainRequest,
  SendingStatusResponse,
  SendingStepsRequest,
  SendingTestResponse,
  SettingsResponse,
  SetupRequest,
  SetupState,
  SetupWelcomeResponse,
  StudioLive,
  StudioTicketResponse,
  SubdomainAvailableResponse,
  TagRequest,
  UpdateAddressRequest,
  UpdateAddressResponse,
  UpdateAutomationRequest,
  UpdateBrandRequest,
  UpdateMeasurementRequest,
  UpgradeInfo,
  UploadedAsset,
  UploadCompleteResponse,
  VideoFromAssetRequest,
  VideoRetryResponse,
  VideoStatusResponse,
  VideoStillsResponse,
  ViewerPhotoResponse,
  WatchComment,
  WatchMessage,
  WatchPageResponse,
  WatchSessionReplay,
  WatchTalkResponse,
  WebhookTestRequest,
  WebhookTestResponse,
  WebinarDetail,
  WebinarListResponse,
  WebinarLive,
  RoomQuestionDraftRequest,
  HostPinRequest,
  HostPinView,
  RoomAudienceResponse,
  RoomGroupKey,
  RoomGroupPeopleResponse,
} from "../../shared/types";
import type { LiveReactionsResponse, ReplayReactionsRequest } from "../../shared/live-reactions";
import { inApp } from "./inApp";
import { emitUpgrade, inAppLimitLine } from "./upgrade";

/** `?session=<id>` for a live call about one session, or nothing for the webinar's current one. */
function sessionQuery(sessionId: string | undefined): string {
  return sessionId ? `?session=${encodeURIComponent(sessionId)}` : "";
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /** Set on a 402: a plan limit was hit, and the app's one line about it is showing. */
    public upgrade?: UpgradeInfo,
    /** Why, when the API said: a good link on the wrong host, or a talk not open yet. */
    public reason?: "wrong_host" | "unpublished" | "no_seat",
    /** Where the page really lives, with "wrong_host". */
    public origin?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** The headers every request carries: the tab's workspace, and JSON when there is a body. */
function requestHeaders(init?: RequestInit): Record<string, string> {
  const headers: Record<string, string> = {};
  const slug = currentWorkspaceSlug();
  if (slug) headers[WORKSPACE_HEADER] = slug;
  if (init?.body) headers["content-type"] = "application/json";
  return { ...headers, ...(init?.headers as Record<string, string> | undefined) };
}

/**
 * Where to send the browser to sign in at Flashsuite, coming back to `to` (a page of the app).
 * Why the app's own address on any other: Flashsuite sends people back there and nowhere else.
 */
export function signInUrl(to: string): string {
  const q = new URLSearchParams({ to });
  const base = typeof window !== "undefined" && isTenantHost(window.location.hostname) ? APP_ORIGIN : "";
  return `${base}/api/auth/start?${q}`;
}

/** Posts that only tell the server someone has read a thread: they change nothing a screen shows. */
const SIGNALS = /\/read$/;

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      credentials: "same-origin",
      ...init,
      headers: requestHeaders(init),
    });
  } catch {
    throw new ApiError(0, "Can't reach the server. Check your connection.");
  }

  const data = (await res.json().catch(() => null)) as unknown;
  // The session ended (Sign out everywhere, say): sign in again and come back here. The auth
  // routes' own 401s are answers their callers read.
  if (res.status === 401 && !path.startsWith("/auth/")) {
    window.location.assign(signInUrl(window.location.pathname + window.location.search));
  }
  if (!res.ok) {
    const err = data as { error?: string; upgrade?: UpgradeInfo; reason?: "wrong_host" | "unpublished" | "no_seat"; origin?: string } | null;
    const message = err?.error ?? "Something went wrong.";
    // A plan limit (402) → the app's one line about it, with the way to Flashsuite;
    // the details ride on the thrown error so callers can stay quiet (no toast).
    if (res.status === 402 && err?.upgrade) {
      const line = inApp() ? inAppLimitLine(err.upgrade.reason, message) : message;
      emitUpgrade({ reason: err.upgrade.reason, message: line });
      throw new ApiError(res.status, line, err.upgrade);
    }
    throw new ApiError(res.status, message, undefined, err?.reason, err?.origin);
  }
  // Why: a change may touch what any screen showed, so nothing kept from before it is shown again.
  if ((init?.method ?? "GET").toUpperCase() !== "GET" && !SIGNALS.test(path)) forgetKept();
  return data as T;
}

/** Worker-proxied file upload (S5): multipart so the browser sets the boundary. */
async function upload<T>(path: string, file: File): Promise<T> {
  const form = new FormData();
  form.append("file", file);
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method: "POST",
      credentials: "same-origin",
      body: form,
    });
  } catch {
    throw new ApiError(0, "Can't reach the server. Check your connection.");
  }
  const data = (await res.json().catch(() => null)) as unknown;
  if (!res.ok) {
    const message =
      (data as { error?: string } | null)?.error ?? "Upload failed.";
    throw new ApiError(res.status, message);
  }
  forgetKept();
  return data as T;
}

/** The public URL a stored R2 key resolves to (same-origin `/api`). */
export function assetUrl(key: string): string {
  return `/api/assets/${key}`;
}

export const api = {
  me: () => request<MeResponse>("/auth/me"),
  // Ends this session, then hands back where the browser goes so Flashsuite's ends too.
  logout: () => request<LogoutResponse>("/auth/logout", { method: "POST" }),
  home: (range: HomeRange = "all") =>
    request<HomeResponse>(`/home?range=${range}`),
  // A Needs a word row put away, until something newer than `seen_at` happens to it.
  homeDone: (body: HomeDoneRequest) =>
    request<OkResponse>("/home/done", { method: "POST", body: JSON.stringify(body) }),
  webinars: {
    list: () => request<WebinarListResponse>("/webinars"),
    // "Go live now": a live made in one step, its link ready at once.
    quickLive: (body: QuickLiveRequest) =>
      request<QuickLiveResponse>("/webinars/quick-live", { method: "POST", body: JSON.stringify(body) }),
    get: (id: string) => request<WebinarDetail>(`/webinars/${id}`),
    // Removes the webinar and everything under it (registrations included).
    remove: (id: string) =>
      request<OkResponse>(`/webinars/${id}`, { method: "DELETE" }),
    // What the room is written in; refused while the talk is being read or the page written.
    setLanguage: (id: string, language: string) =>
      request<RoomLanguageResponse>(`/webinars/${id}/language`, { method: "PUT", body: JSON.stringify({ language } satisfies RoomLanguageRequest) }),
    // The registration confirmation email switch on the detail card.
    // A room without a board keeps its offer on the room itself.
    setOffer: (id: string, offer: OfferJson) =>
      request<OfferResponse>(`/webinars/${id}/offer`, { method: "PUT", body: JSON.stringify({ offer } satisfies OfferRequest) }),
    setConfirmation: (id: string, enabled: boolean) =>
      request<ConfirmationToggleResponse>(`/webinars/${id}/confirmation`, {
        method: "PUT",
        body: JSON.stringify({ enabled } satisfies ConfirmationToggleRequest),
      }),
    // A room's people in groups: a live's last session (or the one named), or all time with "all".
    audience: (id: string, session?: string) =>
      request<RoomAudienceResponse>(`/webinars/${id}/audience${session ? `?session=${encodeURIComponent(session)}` : ""}`),
    // One group's people, a page at a time; `cursor` is the last page's `next`.
    audienceGroup: (id: string, group: RoomGroupKey, session?: string, cursor?: string) => {
      const q = new URLSearchParams();
      if (session) q.set("session", session);
      if (cursor) q.set("cursor", cursor);
      const qs = q.toString();
      return request<RoomGroupPeopleResponse>(`/webinars/${id}/audience/${group}${qs ? `?${qs}` : ""}`);
    },
    // A reply to a question from the live room, drafted to edit; nothing is sent.
    draftAnswer: (id: string, body: RoomQuestionDraftRequest) =>
      request<DraftReplyResponse>(`/webinars/${id}/questions/draft`, { method: "POST", body: JSON.stringify(body) }),
    // Video: reserve a resumable R2 multipart upload, then poll encode status
    // (see src/lib/videoUpload.ts).
    videoFromAsset: (id: string, assetId: string) =>
      request<UploadCompleteResponse>(`/webinars/${id}/video/from-asset`, {
        method: "POST",
        body: JSON.stringify({ asset_id: assetId } satisfies VideoFromAssetRequest),
      }),
    // One session of a series answers its own recording; the webinar's latest without it.
    videoStatus: (id: string, sessionId?: string) =>
      request<VideoStatusResponse>(`/webinars/${id}/video${sessionQuery(sessionId)}`),
    // Restart a FAILED transcode on the recording already in storage, so a capacity
    // failure never costs the host a re-upload. 409 when the job did not fail.
    retryVideo: (id: string) =>
      request<VideoRetryResponse>(`/webinars/${id}/video/retry`, { method: "POST" }),
    // Draw the stills again from the same source: the poster, the sprite sheet
    // and the waveform. For recordings made before the strip drew a wave. It can
    // take minutes on a long talk, so the board asks and forgets.
    videoStills: (id: string) =>
      request<VideoStillsResponse>(`/webinars/${id}/video/stills`, { method: "POST" }),
  },
  // (save), read the talk once (read), run one producer operation on one card
  // (card), and take the page live (publish).
  board: {
    start: (body?: BoardStartRequest) =>
      request<BoardStartResponse>("/webinars/board/start", {
        method: "POST",
        ...(body ? { body: JSON.stringify(body) } : {}),
      }),
    // A live session's draft: its time, what it is about, and the file, if any.
    startLive: (body: BoardStartLiveRequest) =>
      request<BoardStartResponse>("/webinars/board/start-live", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    get: (id: string) => request<BoardStateResponse>(`/webinars/${id}/board`),
    // A live's new time, before it starts: the answer is the time it now has.
    moveLive: (id: string, body: BoardLiveTimeRequest) =>
      request<BoardLive>(`/webinars/${id}/live`, {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    // Change the series from its next session on, or make a one-off a series.
    changeSeries: (id: string, body: LiveSeriesRequest) =>
      request<LiveChangeResponse>(`/webinars/${id}/live/series`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    // End a series early: the sessions still to come are called off, every replay stays.
    endSeries: (id: string) => request<LiveChangeResponse>(`/webinars/${id}/live/series/end`, { method: "POST" }),
    // One session of a series: move it, put it back, skip it, bring it back, or give it a topic.
    changeSession: (id: string, sessionId: string, body: LiveSessionPatch) =>
      request<LiveChangeResponse>(`/webinars/${id}/live/sessions/${encodeURIComponent(sessionId)}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    // How one session went.
    session: (id: string, sessionId: string) =>
      request<WebinarLive>(`/webinars/${id}/live/sessions/${encodeURIComponent(sessionId)}`),
    // `version` must be the one last seen: a mismatch is a 409, meaning the board
    // was saved somewhere else. `init` carries `keepalive` for the leaving flush.
    save: (id: string, body: BoardSaveRequest, init: RequestInit = {}) =>
      request<BoardSaveResponse>(`/webinars/${id}/board`, {
        method: "PUT",
        body: JSON.stringify(body),
        ...init,
      }),
    // The names check: what the talk repeats, and the host's corrections. The
    // gate between the transcript landing and the read being asked for.
    names: (id: string) => request<BoardNamesResponse>(`/webinars/${id}/board/names`),
    // The talk's own lines, so the composer can show what was said at a second.
    lines: (id: string) => request<BoardLinesResponse>(`/webinars/${id}/board/lines`),
    fixNames: (id: string, fixes: Array<{ from: string; to: string }>) =>
      request<BoardNamesResponse>(`/webinars/${id}/board/names`, {
        method: "POST",
        body: JSON.stringify({ fixes } satisfies BoardNamesFixRequest),
      }),
    // One pass over the talk. 409 when it has already been read; `force` replaces
    // the whole board (the client confirms first).
    chapters: (id: string, body: BoardChaptersRequest) =>
      request<BoardStateResponse>(`/webinars/${id}/board/chapters`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    read: (id: string, body: BoardReadRequest = {}) =>
      request<BoardStateResponse>(`/webinars/${id}/board/read`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    // One producer operation (moment / candidates /
    // followup) → the board the result landed on, plus the card it wrote.
    card: (id: string, body: BoardCardRequest) =>
      request<BoardCardResponse>(`/webinars/${id}/board/card`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    publish: (id: string) =>
      request<BoardPublishResponse>(`/webinars/${id}/board/publish`, { method: "POST" }),
    unpublish: (id: string) =>
      request<BoardUnpublishResponse>(`/webinars/${id}/board/unpublish`, { method: "POST" }),
    // The recording's words, when the pipeline did not write them itself.
    transcribe: (id: string, body: BoardTranscribeRequest) =>
      request<BoardTranscribeResponse>(`/webinars/${id}/board/transcribe`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },
  // The page a read ends on (and every page made after), and the one chosen.
  pages: {
    list: (id: string) => request<PagePairsResponse>(`/webinars/${id}/pages`),
    make: (id: string, body?: PageMakeRequest) =>
      request<PageMakeResponse>(`/webinars/${id}/pages/make`, {
        method: "POST",
        ...(body ? { body: JSON.stringify(body) } : {}),
      }),
    choose: (id: string, body: PageChooseRequest) =>
      request<PageStudioResponse>(`/webinars/${id}/pages/choose`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    inspirations: (id: string, body: PageInspirationsRequest) =>
      request<PageInspirationsResponse>(`/webinars/${id}/page/inspirations`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    readPicture: (id: string, slot: PageSlot) =>
      request<PageInspirationsResponse>(`/webinars/${id}/page/inspiration/${slot}/read`, { method: "POST", body: "{}" }),
  },
  // The Page tab: the page and its threads, and every way it changes.
  page: {
    get: (id: string) => request<PageStudioResponse>(`/webinars/${id}/page`),
    videos: (id: string) => request<PageVideosResponse>(`/webinars/${id}/page/videos`),
    ask: (id: string, body: PageAskRequest) =>
      request<PageAskResponse>(`/webinars/${id}/page/ask`, { method: "POST", body: JSON.stringify(body) }),
    text: (id: string, body: PageTextRequest) =>
      request<PageArrangeResponse>(`/webinars/${id}/page/text`, { method: "POST", body: JSON.stringify(body) }),
    back: (id: string, body: PageBackRequest) =>
      request<PageBackResponse>(`/webinars/${id}/page/back`, { method: "POST", body: JSON.stringify(body) }),
    // The Phone switch on a room's page: the form asks for a phone, or stops.
    phone: (id: string, on: boolean) =>
      request<PagePhoneResponse>(`/webinars/${id}/page/phone`, { method: "POST", body: JSON.stringify({ on } satisfies PagePhoneRequest) }),
  },
  // The card a pasted link shows: what it says now, what the host pinned, and
  // one model call that writes the words from the board.
  share: {
  },
  people: {
    list: (filters: PeopleFilters = {}) => {
      const p = new URLSearchParams();
      if (filters.group) p.set("group", filters.group);
      if (filters.room) p.set("room", filters.room);
      if (filters.stage) p.set("stage", filters.stage);
      if (filters.range) p.set("range", filters.range);
      if (filters.q) p.set("q", filters.q);
      for (const w of filters.webinar ?? []) p.append("webinar", w);
      const qs = p.toString();
      return request<PeopleListResponse>(`/registrants${qs ? `?${qs}` : ""}`);
    },
    get: (id: string) => request<PersonDetailResponse>(`/registrants/${id}`),
    addNote: (id: string, body: NoteRequest) =>
      request<PersonNote>(`/registrants/${id}/notes`, { method: "POST", body: JSON.stringify(body) }),
    // Deletes the person: every registration, their contact and the notes on them.
    remove: (id: string) => request<OkResponse>(`/registrants/${id}`, { method: "DELETE" }),
    enrollMany: (body: EnrollManyRequest) =>
      request<EnrollManyResponse>("/registrants/automation", { method: "POST", body: JSON.stringify(body) }),
    // One note to many people, each into their own thread.
    email: (body: PeopleEmailRequest) =>
      request<PeopleEmailResponse>("/registrants/email", { method: "POST", body: JSON.stringify(body) }),
    emailDraft: (body: PeopleEmailDraftRequest) =>
      request<PeopleEmailDraft>("/registrants/email/draft", { method: "POST", body: JSON.stringify(body) }),
    tag: (id: string, body: TagRequest) =>
      request<OkResponse>(`/registrants/${id}/tag`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },
  invites: {
    options: (webinarId: string) => request<InviteOptions>(`/webinars/${webinarId}/invites/options`),
    preview: (webinarId: string, audience: InviteAudience, init: RequestInit = {}) =>
      request<InvitePreview>(`/webinars/${webinarId}/invites/preview`, {
        ...init,
        method: "POST",
        body: JSON.stringify({ audience }),
      }),
    send: (webinarId: string, body: InviteSendRequest) =>
      request<InviteSendResponse>(`/webinars/${webinarId}/invites`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    test: (webinarId: string, body: InviteTestRequest) =>
      request<{ ok: true; to: string }>(`/webinars/${webinarId}/invites/test`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    draft: (webinarId: string, current: InviteDraft) =>
      request<InviteDraft>(`/webinars/${webinarId}/invites/draft`, {
        method: "POST",
        body: JSON.stringify(current),
      }),
    list: (webinarId: string) => request<InvitesResponse>(`/webinars/${webinarId}/invites`),
    remind: (webinarId: string, sendId: string) =>
      request<InviteSendResponse>(`/webinars/${webinarId}/invites/${sendId}/remind`, { method: "POST" }),
  },
  automations: {
    list: () => request<AutomationListResponse>("/automations"),
    get: (id: string) => request<AutomationDetail>(`/automations/${id}`),
    create: (body: CreateAutomationRequest) =>
      request<AutomationDetail>("/automations", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    update: (id: string, body: UpdateAutomationRequest, init: RequestInit = {}) =>
      request<AutomationDetail>(`/automations/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
        ...init,
      }),
    saveWebinars: (id: string, body: PutAutomationWebinarsRequest) =>
      request<AutomationDetail>(`/automations/${id}/webinars`, {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    // The whole flow, as one graph. `init` carries `keepalive` when the write
    // is the one a leaving tab owes.
    saveGraph: (id: string, body: SaveGraphRequest, init: RequestInit = {}) =>
      request<OkResponse>(`/automations/${id}/graph`, {
        method: "PUT",
        body: JSON.stringify(body),
        ...init,
      }),
    factCatalog: () => request<FactCatalog>("/facts/catalog"),
    remove: (id: string) => request<OkResponse>(`/automations/${id}`, { method: "DELETE" }),
    // Who is where in the flow.
    people: (id: string) => request<AutomationPeople>(`/automations/${id}/people`),
    // The two things a host can do about a run that stopped part-way.
    retryRun: (id: string, runId: string) =>
      request<AutomationDetail>(`/automations/${id}/runs/${runId}/retry`, { method: "POST" }),
    stopRun: (id: string, runId: string) =>
      request<AutomationDetail>(`/automations/${id}/runs/${runId}/stop`, { method: "POST" }),
  },
  // The ingest key (a product's front door): issue/rotate from Settings.
  ingest: {
    key: (scope: "full" | "read" = "full") => request<ApiKeyInfo>(`/ingest/key${scope === "read" ? "?scope=read" : ""}`),
    rotateKey: (scope: "full" | "read" = "full") =>
      request<IssuedKeyResponse>(`/ingest/key/rotate${scope === "read" ? "?scope=read" : ""}`, { method: "POST" }),
  },
  // Public funnel (unauth, tenant-scoped by host): how this host's workspace looks.
  public: {
    look: () => request<PublicLookResponse>("/public/look"),
    // A quick live's link: the talk, and who this browser already is here.
    door: (slug: string, tokens: string[]) =>
      request<LiveDoorResponse>(`/w/${encodeURIComponent(slug)}/door`, {
        method: "POST",
        body: JSON.stringify({ tokens }),
      }),
    register: (slug: string, body: PublicRegisterRequest) =>
      request<PublicRegisterResponse>(`/w/${encodeURIComponent(slug)}/register`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    joinAs: (slug: string, token: string, attribution: Record<string, unknown>) =>
      request<PublicRegisterResponse>(`/w/${encodeURIComponent(slug)}/join-as`, {
        method: "POST",
        body: JSON.stringify({ token, attribution }),
      }),
  },
  // The living watch page (unauth, scoped by the permanent watch token).
  watch: {
    page: (token: string) =>
      request<WatchPageResponse>(`/watch/${encodeURIComponent(token)}`),
    progress: (token: string, body: ProgressRequest) =>
      request<OkResponse>(`/watch/${encodeURIComponent(token)}/progress`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    respond: (token: string, body: RespondRequest) =>
      request<OkResponse>(`/watch/${encodeURIComponent(token)}/respond`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    react: (token: string, postId: string) =>
      request<ReactResponse>(
        `/watch/${encodeURIComponent(token)}/posts/${encodeURIComponent(postId)}/react`,
        { method: "POST" },
      ),
    comment: (token: string, postId: string, body: PublicCommentRequest) =>
      request<WatchComment>(
        `/watch/${encodeURIComponent(token)}/posts/${encodeURIComponent(postId)}/comment`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    message: (token: string, body: PublicMessageRequest) =>
      request<WatchMessage>(`/watch/${encodeURIComponent(token)}/message`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    // A file the registrant attaches: token scoped, so it needs no session.
    upload: (token: string, file: File) =>
      upload<UploadedAsset>(`/watch/${encodeURIComponent(token)}/upload`, file),
    // The viewer's own photo, set from their Room and shown to the host.
    setPhoto: (token: string, file: File) =>
      upload<ViewerPhotoResponse>(`/watch/${encodeURIComponent(token)}/photo`, file),
    clearPhoto: (token: string) =>
      request<ViewerPhotoResponse>(`/watch/${encodeURIComponent(token)}/photo`, { method: "DELETE" }),
    // They pressed play on the offer's video, or it played to the end.
    offerVideo: (token: string, body: OfferVideoRequest) =>
      request<OkResponse>(`/watch/${encodeURIComponent(token)}/offer-video`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    // The conversion signal: clicking the offer signs them up (→ webhook handoff).
    signup: (token: string, body: { webinar_id: string }) =>
      request<OkResponse>(`/watch/${encodeURIComponent(token)}/signup`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    // A ticket into a live's room: its picture, sound and group chat.
    liveTicket: (token: string, body: LiveTicketRequest) =>
      request<LiveTicketResponse>(`/watch/${encodeURIComponent(token)}/live-ticket`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    // The way into the session's meeting, where everyone is on camera.
    liveJoin: (token: string, body: LiveTicketRequest) =>
      request<LiveJoinResponse>(`/watch/${encodeURIComponent(token)}/live/join`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    // Reactions made on a live's replay, in batches. `keepalive` carries the last
    // batch past the page closing, up to 64 KiB (MDN, RequestInit, read 19 Sep 2026).
    reactions: (token: string, body: ReplayReactionsRequest, keepalive = false) =>
      request<OkResponse>(`/watch/${encodeURIComponent(token)}/reactions`, {
        method: "POST",
        body: JSON.stringify(body),
        ...(keepalive ? { keepalive: true } : {}),
      }),
    // The recorded room's frames and words, asked for once the room opens; with a session, that replay's words.
    talk: (token: string, webinarId: string, sessionId?: string) =>
      request<WatchTalkResponse>(
        `/watch/${encodeURIComponent(token)}/talk/${encodeURIComponent(webinarId)}${sessionId ? `?session=${encodeURIComponent(sessionId)}` : ""}`,
      ),
    // One session of a series: its replay, chapters, chat and this person's place in it.
    sessionReplay: (token: string, sessionId: string) =>
      request<WatchSessionReplay>(
        `/watch/${encodeURIComponent(token)}/sessions/${encodeURIComponent(sessionId)}`,
      ),
    leaveSeries: (token: string, body: LeaveSeriesRequest) =>
      request<OkResponse>(`/watch/${encodeURIComponent(token)}/leave-series`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    pin: (token: string, body: PinRequest) =>
      request<WatchPin>(`/watch/${encodeURIComponent(token)}/pins`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    unpin: (token: string, pinId: string) =>
      request<OkResponse>(`/watch/${encodeURIComponent(token)}/pins/${encodeURIComponent(pinId)}`, { method: "DELETE" }),
    seePin: (token: string, pinId: string) =>
      request<OkResponse>(`/watch/${encodeURIComponent(token)}/pins/${encodeURIComponent(pinId)}/seen`, { method: "POST" }),
    // Their heart on a host message, and a change to one of their own.
    love: (token: string, messageId: string) =>
      request<LoveResponse>(`/watch/${encodeURIComponent(token)}/messages/${encodeURIComponent(messageId)}/love`, {
        method: "POST",
      }),
    editMessage: (token: string, messageId: string, body: EditMessageRequest) =>
      request<OkResponse>(`/watch/${encodeURIComponent(token)}/messages/${encodeURIComponent(messageId)}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    // A sign-up answer they change on the Answers tab.
    answer: (token: string, body: SignupAnswerRequest) =>
      request<OkResponse>(`/watch/${encodeURIComponent(token)}/answers`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    // A video they record for the host: the same parts upload a host's note takes, owned by them.
    notes: {
      start: (token: string, body: NoteUploadStartRequest) =>
        request<NoteUploadStartResponse>(`/watch/${encodeURIComponent(token)}/notes`, {
          method: "POST",
          body: JSON.stringify(body),
        }),
      sign: (token: string, body: NoteUploadSignRequest) =>
        request<NoteUploadSignResponse>(`/watch/${encodeURIComponent(token)}/notes/upload/sign`, {
          method: "POST",
          body: JSON.stringify(body),
        }),
      complete: (token: string, body: NoteUploadCompleteRequest) =>
        request<NoteUploadCompleteResponse>(`/watch/${encodeURIComponent(token)}/notes/upload/complete`, {
          method: "POST",
          body: JSON.stringify(body),
        }),
      abort: (token: string, body: NoteUploadAbortRequest, opts?: { keepalive?: boolean }) =>
        request<OkResponse>(`/watch/${encodeURIComponent(token)}/notes/upload/abort`, {
          method: "POST",
          body: JSON.stringify(body),
          keepalive: opts?.keepalive,
        }),
      get: (token: string, noteId: string) =>
        request<NoteStatusResponse>(`/watch/${encodeURIComponent(token)}/notes/${encodeURIComponent(noteId)}`),
    },
  },
  // The host's studio for a live (node L5): what it opens with, a fresh ticket
  // for a reconnect, go live, end, and who is registered and here.
  // Every call but `get` and `ticket` takes the session it is for; a webinar's
  // current session when it is left out.
  studio: {
    get: (webinarId: string) => request<LiveStudio>(`/webinars/${encodeURIComponent(webinarId)}/studio`),
    ticket: (webinarId: string) =>
      request<StudioTicketResponse>(`/webinars/${encodeURIComponent(webinarId)}/studio/ticket`, { method: "POST" }),
    start: (webinarId: string, sessionId?: string) =>
      request<StudioLive>(`/webinars/${encodeURIComponent(webinarId)}/live/start${sessionQuery(sessionId)}`, {
        method: "POST",
      }),
    end: (webinarId: string, sessionId?: string) =>
      request<LiveEndResponse>(`/webinars/${encodeURIComponent(webinarId)}/live/end${sessionQuery(sessionId)}`, {
        method: "POST",
      }),
    pauseRecording: (webinarId: string, paused: boolean, sessionId?: string) =>
      request<void>(`/webinars/${encodeURIComponent(webinarId)}/live/recording${sessionQuery(sessionId)}`, {
        method: "POST",
        body: JSON.stringify({ paused }),
      }),
    // A planned moment, started early, skipped, or closed.
    moment: (webinarId: string, momentId: string, body: LiveMomentRequest, sessionId?: string) =>
      request<LiveMomentResponse>(
        `/webinars/${encodeURIComponent(webinarId)}/live/moments/${encodeURIComponent(momentId)}${sessionQuery(sessionId)}`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    // Everyone registered for the session, the people here first.
    people: (webinarId: string, sessionId?: string) =>
      request<LivePeopleResponse>(`/webinars/${encodeURIComponent(webinarId)}/live/people${sessionQuery(sessionId)}`),
    // The host's way into the session's meeting.
    join: (webinarId: string, sessionId?: string) =>
      request<LiveJoinResponse>(`/webinars/${encodeURIComponent(webinarId)}/live/join${sessionQuery(sessionId)}`, {
        method: "POST",
      }),
    // After the live: what they loved, for everyone, the live or the replay.
    reactions: (webinarId: string, source: "all" | "live" | "replay", sessionId?: string) =>
      request<LiveReactionsResponse>(
        `/webinars/${encodeURIComponent(webinarId)}/live/reactions?source=${source}${sessionId ? `&session=${encodeURIComponent(sessionId)}` : ""}`,
      ),
  },
  // Conversations (notes/talk/PLAN.md): each room's Everyone, Announcements, Files and circles,
  // Direct with chat and email in one thread, and one search.
  talk: {
    rail: () => request<HostTalkResponse>("/talk"),
    room: (webinarId: string) => request<HostRoomRow>(`/talk/rooms/${encodeURIComponent(webinarId)}`),
    keep: (webinarId: string, on: boolean) =>
      request<OkResponse>(`/talk/rooms/${encodeURIComponent(webinarId)}/keep`, { method: on ? "PUT" : "DELETE" }),
    everyone: (webinarId: string) => request<HostEveryoneResponse>(`/talk/rooms/${encodeURIComponent(webinarId)}/everyone`),
    sendEveryone: (webinarId: string, body: TalkSendRequest) =>
      request<TalkMessage>(`/talk/rooms/${encodeURIComponent(webinarId)}/everyone`, { method: "POST", body: JSON.stringify(body) }),
    settings: (webinarId: string, body: { everyoneChat: boolean }) =>
      request<OkResponse>(`/talk/rooms/${encodeURIComponent(webinarId)}/settings`, { method: "PUT", body: JSON.stringify(body) }),
    hold: (webinarId: string, body: { registrantId: string; kind: "paused" | "removed" }) =>
      request<OkResponse>(`/talk/rooms/${encodeURIComponent(webinarId)}/holds`, { method: "POST", body: JSON.stringify(body) }),
    unhold: (webinarId: string, registrantId: string) =>
      request<OkResponse>(`/talk/rooms/${encodeURIComponent(webinarId)}/holds/${encodeURIComponent(registrantId)}`, { method: "DELETE" }),
    heart: (messageId: string, on: boolean) =>
      request<OkResponse>(`/talk/messages/${encodeURIComponent(messageId)}/heart`, { method: on ? "POST" : "DELETE" }),
    hide: (messageId: string) => request<OkResponse>(`/talk/messages/${encodeURIComponent(messageId)}/hide`, { method: "POST" }),
    pin: (messageId: string, on: boolean) =>
      request<OkResponse>(`/talk/messages/${encodeURIComponent(messageId)}/pin`, { method: on ? "POST" : "DELETE" }),
    announcements: (webinarId: string) =>
      request<HostAnnouncementsResponse>(`/talk/rooms/${encodeURIComponent(webinarId)}/announcements`),
    announce: (webinarId: string, body: AnnounceRequest) =>
      request<TalkAnnouncement>(`/talk/rooms/${encodeURIComponent(webinarId)}/announcements`, { method: "POST", body: JSON.stringify(body) }),
    unannounce: (postId: string) => request<OkResponse>(`/talk/announcements/${encodeURIComponent(postId)}`, { method: "DELETE" }),
    files: (webinarId: string) => request<HostFilesResponse>(`/talk/rooms/${encodeURIComponent(webinarId)}/files`),
    pinFile: (webinarId: string, key: string) =>
      request<OkResponse>(`/talk/rooms/${encodeURIComponent(webinarId)}/files/pin`, { method: "POST", body: JSON.stringify({ key }) }),
    createCircle: (body: CircleCreateRequest) =>
      request<CircleCreateResponse>("/talk/circles", { method: "POST", body: JSON.stringify(body) }),
    circle: (circleId: string) => request<HostCircleResponse>(`/talk/circles/${encodeURIComponent(circleId)}`),
    sendCircle: (circleId: string, body: TalkSendRequest) =>
      request<TalkMessage>(`/talk/circles/${encodeURIComponent(circleId)}/messages`, { method: "POST", body: JSON.stringify(body) }),
    editCircle: (circleId: string, body: { name?: string; membersCanTalk?: boolean }) =>
      request<OkResponse>(`/talk/circles/${encodeURIComponent(circleId)}`, { method: "PATCH", body: JSON.stringify(body) }),
    goLive: (circleId: string) =>
      request<{ webinarId: string; liveSessionId: string; studioPath: string }>(`/talk/circles/${encodeURIComponent(circleId)}/live`, { method: "POST" }),
    nextCall: (circleId: string, body: { startsAt: number; lengthMin: number }) =>
      request<OkResponse>(`/talk/circles/${encodeURIComponent(circleId)}/next-call`, { method: "PUT", body: JSON.stringify(body) }),
    direct: (contactId: string) => request<HostDirectResponse>(`/talk/direct/${encodeURIComponent(contactId)}`),
    directFor: (registrantId: string) =>
      request<{ contactId: string }>(`/talk/direct/for/${encodeURIComponent(registrantId)}`, { method: "POST" }),
    sendDirect: (contactId: string, body: DirectSendRequest) =>
      request<OkResponse>(`/talk/direct/${encodeURIComponent(contactId)}`, { method: "POST", body: JSON.stringify(body) }),
    // Draft with AI: a warm reply to edit, dropped into the composer and never sent.
    draftDirect: (contactId: string) =>
      request<DraftReplyResponse>(`/talk/direct/${encodeURIComponent(contactId)}/draft`, { method: "POST" }),
    heartDirect: (contactId: string, messageId: string, on: boolean) =>
      request<OkResponse>(`/talk/direct/${encodeURIComponent(contactId)}/messages/${encodeURIComponent(messageId)}/heart`, {
        method: on ? "POST" : "DELETE",
      }),
    readDirect: (contactId: string, upTo: number) =>
      request<OkResponse>(`/talk/direct/${encodeURIComponent(contactId)}/read`, { method: "POST", body: JSON.stringify({ upTo }) }),
    seen: (place: string, upTo?: number) =>
      request<OkResponse>("/talk/seen", { method: "POST", body: JSON.stringify({ place, upTo } satisfies SeenRequest) }),
    questions: (webinarId: string) => request<TalkQuestionsResponse>(`/talk/rooms/${encodeURIComponent(webinarId)}/questions`),
    notSame: (webinarId: string, messageId: string) =>
      request<OkResponse>(`/talk/rooms/${encodeURIComponent(webinarId)}/questions/${encodeURIComponent(messageId)}/not-same`, { method: "POST" }),
    // One answer drafted for the questions the host sees, with no name in it; nothing is sent.
    draftQuestions: (webinarId: string, body: QuestionDraftRequest) =>
      request<QuestionDraftResponse>(`/talk/rooms/${encodeURIComponent(webinarId)}/questions/draft`, { method: "POST", body: JSON.stringify(body) }),
    answerQuestions: (webinarId: string, body: QuestionAnswerRequest) =>
      request<QuestionAnswerResponse>(`/talk/rooms/${encodeURIComponent(webinarId)}/questions/answer`, { method: "POST", body: JSON.stringify(body) }),
    answers: (webinarId: string) => request<TalkAnswersResponse>(`/talk/rooms/${encodeURIComponent(webinarId)}/answers`),
    answerPeople: (group: TalkAnswerGroup, cursor?: string) =>
      request<RoomGroupPeopleResponse>(
        `/talk/rooms/${encodeURIComponent(group.webinarId)}/answers/${encodeURIComponent(group.interactionId)}/people?option=${encodeURIComponent(group.option)}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
      ),
    sendPrivately: (body: PrivateSendRequest) =>
      request<PrivateSendResponse>("/talk/private", { method: "POST", body: JSON.stringify(body) }),
    search: (q: string) => request<TalkSearchResponse>(`/talk/search?q=${encodeURIComponent(q)}`),
  },
  // The guest's own page beyond the private thread (notes/talk/PLAN.md).
  guestTalk: {
    get: (token: string) => request<GuestTalkResponse>(`/watch/${encodeURIComponent(token)}/talk`),
    everyone: (token: string, webinarId: string) =>
      request<GuestEveryoneResponse>(`/watch/${encodeURIComponent(token)}/everyone/${encodeURIComponent(webinarId)}`),
    sendEveryone: (token: string, webinarId: string, body: TalkSendRequest) =>
      request<TalkMessage>(`/watch/${encodeURIComponent(token)}/everyone/${encodeURIComponent(webinarId)}`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    announcements: (token: string, webinarId: string) =>
      request<GuestAnnouncementsResponse>(`/watch/${encodeURIComponent(token)}/announcements/${encodeURIComponent(webinarId)}`),
    heartPost: (token: string, postId: string, on: boolean) =>
      request<OkResponse>(`/watch/${encodeURIComponent(token)}/announcements/${encodeURIComponent(postId)}/heart`, {
        method: on ? "POST" : "DELETE",
      }),
    circle: (token: string, circleId: string) =>
      request<GuestCircleResponse>(`/watch/${encodeURIComponent(token)}/circles/${encodeURIComponent(circleId)}`),
    sendCircle: (token: string, circleId: string, body: TalkSendRequest) =>
      request<TalkMessage>(`/watch/${encodeURIComponent(token)}/circles/${encodeURIComponent(circleId)}`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    heart: (token: string, messageId: string, on: boolean) =>
      request<OkResponse>(`/watch/${encodeURIComponent(token)}/room-messages/${encodeURIComponent(messageId)}/heart`, {
        method: on ? "POST" : "DELETE",
      }),
    vote: (token: string, messageId: string, choice: number) =>
      request<OkResponse>(`/watch/${encodeURIComponent(token)}/room-messages/${encodeURIComponent(messageId)}/vote`, {
        method: "POST",
        body: JSON.stringify({ choice }),
      }),
    rsvp: (token: string, circleId: string, coming: boolean) =>
      request<OkResponse>(`/watch/${encodeURIComponent(token)}/circles/${encodeURIComponent(circleId)}/rsvp`, {
        method: "PUT",
        body: JSON.stringify({ coming }),
      }),
    files: (token: string) => request<{ files: TalkFile[] }>(`/watch/${encodeURIComponent(token)}/files`),
    opened: (token: string, key: string) =>
      request<OkResponse>(`/watch/${encodeURIComponent(token)}/files/open`, { method: "POST", body: JSON.stringify({ key }) }),
    search: (token: string, q: string) =>
      request<TalkSearchResponse>(`/watch/${encodeURIComponent(token)}/search?q=${encodeURIComponent(q)}`),
    seen: (token: string, place: GuestPlace, upTo?: number) =>
      request<OkResponse>(`/watch/${encodeURIComponent(token)}/seen`, {
        method: "POST",
        body: JSON.stringify({ place, upTo } satisfies SeenRequest),
      }),
    prefs: (token: string, body: Partial<GuestPrefs>) =>
      request<GuestPrefs>(`/watch/${encodeURIComponent(token)}/prefs`, { method: "PUT", body: JSON.stringify(body) }),
  },
  // What is pinned at the top of a person's page: their page's Pinned column reads the same pins.
  pins: {
    forPerson: (registrantId: string) =>
      request<{ pins: HostPinView[] }>(`/pins?registrant_id=${encodeURIComponent(registrantId)}`),
    add: (body: HostPinRequest) => request<HostPinView>("/pins", { method: "POST", body: JSON.stringify(body) }),
    remove: (id: string) => request<OkResponse>(`/pins/${encodeURIComponent(id)}`, { method: "DELETE" }),
  },
  settings: {
    get: () => request<SettingsResponse>("/settings"),
    measurement: (body: UpdateMeasurementRequest) =>
      request<SettingsResponse>("/settings/measurement", { method: "PUT", body: JSON.stringify(body) }),
    addScript: (body: ScriptRequest) => request<SettingsResponse>("/settings/scripts", { method: "POST", body: JSON.stringify(body) }),
    updateScript: (id: string, body: ScriptRequest) =>
      request<SettingsResponse>(`/settings/scripts/${encodeURIComponent(id)}`, { method: "PUT", body: JSON.stringify(body) }),
    setMainWebinar: (webinarId: string | null) =>
      request<SettingsResponse>("/settings/main-webinar", { method: "PUT", body: JSON.stringify({ webinar_id: webinarId }) }),
    saveAutomations: (body: AutomationsSettingsRequest) =>
      request<SettingsResponse>("/settings/automations", { method: "PUT", body: JSON.stringify(body) }),
    updateBrand: (body: UpdateBrandRequest) =>
      request<SettingsResponse>("/settings/brand", {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    // The web address: whether one is free, and moving to it.
    addressAvailable: (slug: string) =>
      request<SubdomainAvailableResponse>(`/settings/address/available?slug=${encodeURIComponent(slug)}`),
    updateAddress: (body: UpdateAddressRequest) =>
      request<UpdateAddressResponse>("/settings/address", { method: "PUT", body: JSON.stringify(body) }),
    addDomain: (body: CustomDomainRequest) =>
      request<SettingsResponse>("/settings/domains", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    verifyDomain: () =>
      request<SettingsResponse>("/settings/domains/verify", { method: "POST" }),
    removeDomain: () =>
      request<SettingsResponse>("/settings/domains", { method: "DELETE" }),
    addSending: (body: SendingDomainRequest & { from_email?: string }) =>
      request<SettingsResponse>("/settings/sending", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    verifySending: () =>
      request<SettingsResponse>("/settings/sending/verify", { method: "POST" }),
    turnOffSending: () =>
      request<SettingsResponse>("/settings/sending", { method: "DELETE" }),
    // Sending protection (B3): resume a paused/final workspace into the watched
    // recovery trial, and message the platform team from a status page.
    resumeSending: () =>
      request<SettingsResponse>("/settings/sending/resume", { method: "POST" }),
    sendTest: () => request<SendingTestResponse>("/settings/sending/test", { method: "POST" }),
    sendSteps: (body: SendingStepsRequest) =>
      request<SettingsResponse>("/settings/sending/steps", { method: "POST", body: JSON.stringify(body) }),
    // First-run setup: one reading for the setup screens, the top bar's pill, Settings and Home.
    setup: () => request<SetupState>("/settings/setup"),
    welcome: () => request<SetupWelcomeResponse>("/settings/setup/welcome"),
    markSetup: (body: SetupRequest) => request<SetupState>("/settings/setup", { method: "PUT", body: JSON.stringify(body) }),
  },
  // The records a business asked someone to add (unauth, scoped by the link's token).
  records: {
    get: (token: string) => request<PublicRecordsResponse>(`/records/${encodeURIComponent(token)}`),
  },
  // Sending protection (B3): the compact, app-wide escalation state the global
  // banner + Settings nav dot read, loaded once at app start, refreshed on resume.
  sending: {
    status: () => request<SendingStatusResponse>("/sending/status"),
  },
  // Content / R2 (S5): upload post / message / handout assets, unfurl link cards.
  content: {
  },
  // Assets: the person's one library, shown in every business. Files go up through `src/assets/upload.ts`.
  library: {
    list: () => request<LibraryResponse>("/library"),
    one: (id: string) => request<LibraryAsset>(`/library/assets/${encodeURIComponent(id)}`),
    use: (id: string) => request<LibraryUseResponse>(`/library/assets/${encodeURIComponent(id)}/use`, { method: "POST" }),
    folder: (body: LibraryFolderRequest) =>
      request<LibraryFolder>("/library/folders", { method: "POST", body: JSON.stringify(body) }),
    patchFolder: (id: string, body: LibraryFolderPatch) =>
      request<LibraryFolder>(`/library/folders/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }),
    removeFolder: (id: string) => request<null>(`/library/folders/${encodeURIComponent(id)}`, { method: "DELETE" }),
    patch: (id: string, body: LibraryAssetPatch) =>
      request<LibraryAsset>(`/library/assets/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }),
    remove: (id: string) => request<null>(`/library/assets/${encodeURIComponent(id)}`, { method: "DELETE" }),
    restore: (id: string) => request<LibraryAsset>(`/library/assets/${encodeURIComponent(id)}/restore`, { method: "POST" }),
    startUpload: (body: LibraryUploadStartRequest) =>
      request<LibraryUploadStartResponse>("/library/uploads", { method: "POST", body: JSON.stringify(body) }),
    completeUpload: (id: string, body: LibraryUploadCompleteRequest) =>
      request<LibraryAsset>(`/library/uploads/${encodeURIComponent(id)}/complete`, { method: "POST", body: JSON.stringify(body) }),
    giveUpUpload: (id: string) => request<null>(`/library/uploads/${encodeURIComponent(id)}`, { method: "DELETE" }),
  },
  // Recorded notes (N1/N2): the composer records a voice or video/screen note and
  // uploads it presigned direct-to-R2, then polls the transcode. Mirrors the video
  // upload path; the flow lives in src/lib/noteUpload.ts.
  notes: {
    start: (body: NoteUploadStartRequest) =>
      request<NoteUploadStartResponse>("/notes", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    sign: (body: NoteUploadSignRequest) =>
      request<NoteUploadSignResponse>("/notes/upload/sign", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    complete: (body: NoteUploadCompleteRequest) =>
      request<NoteUploadCompleteResponse>("/notes/upload/complete", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    abort: (body: NoteUploadAbortRequest, opts?: { keepalive?: boolean }) =>
      request<OkResponse>("/notes/upload/abort", {
        method: "POST",
        body: JSON.stringify(body),
        keepalive: opts?.keepalive,
      }),
    get: (id: string) => request<NoteStatusResponse>(`/notes/${id}`),
  },
  connections: {
    // Send a sample SIGNED payload so a user can verify their webhook endpoint.
    testWebhook: (body: WebhookTestRequest) =>
      request<WebhookTestResponse>("/connections/webhook/test", {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },
  // The plan is Flashsuite's to show and change; the app only reads what its screens say.
  billing: {
    liveNotice: (since: number) =>
      request<BillingLiveNoticeResponse>(`/billing/live-notice?since=${encodeURIComponent(String(since))}`),
    alerts: () => request<BillingAlertResponse>("/billing/alerts"),
  },
};
